/**
 * Fallback service that provides mock data when the server is unavailable
 * This allows development to continue even when the backend is not accessible
 */
import { mockUsers, mockSites, mockDevices, mockReadings, mockDailyUsage } from './mockData';

const fallbackService = {
  /**
   * Authentication fallback
   */
  auth: {
    login(username, password) {
      console.log('Using fallback auth service for login');
      
      // Determine role based on username for testing different roles
      let role = 'end_user'; // Default role
      if (username.includes('admin')) {
        role = 'admin';
      } else if (username.includes('operator')) {
        role = 'operator';
      } else if (username.includes('super')) {
        role = 'super_user';
      }
      
      // Mock user data
      const user = {
        id: '1',
        username: username,
        name: username.charAt(0).toUpperCase() + username.slice(1) + ' User',
        email: `${username}@example.com`,
        role: role
      };
      
      // Set permissions based on role
      let permissions = {};
      
      if (role === 'admin') {
        permissions = {
          canViewAllSites: true,
          canEditSites: true,
          canDeleteSites: true,
          canViewAllDevices: true,
          canEditDevices: true,
          canDeleteDevices: true,
          canManageUsers: true,
          canViewReports: true,
          canViewBilling: true
        };
      } else if (role === 'operator') {
        permissions = {
          canViewAllSites: true,
          canEditSites: true,
          canDeleteSites: false,
          canViewAllDevices: true,
          canEditDevices: true,
          canDeleteDevices: false,
          canManageUsers: true,
          canViewReports: true,
          canViewBilling: true
        };
      } else if (role === 'super_user') {
        permissions = {
          canViewAllSites: true,
          canEditSites: true,
          canDeleteSites: false,
          canViewAllDevices: true,
          canEditDevices: true,
          canDeleteDevices: false,
          canManageUsers: false,
          canViewReports: true,
          canViewBilling: true
        };
      } else { // end_user
        permissions = {
          canViewAllSites: false,
          canEditSites: false,
          canDeleteSites: false,
          canViewAllDevices: true,
          canEditDevices: false,
          canDeleteDevices: false,
          canManageUsers: false,
          canViewReports: true,
          canViewBilling: false
        };
      }
      
      return {
        user,
        token: 'mock-jwt-token-' + Date.now(),
        permissions
      };
    },
    
    verifyToken() {
      console.log('Using fallback auth service for token verification');
      const storedUser = localStorage.getItem('user');
      return storedUser ? JSON.parse(storedUser) : null;
    },
    
    getPermissions() {
      console.log('Using fallback auth service for permissions');
      const storedPermissions = localStorage.getItem('permissions');
      return storedPermissions ? JSON.parse(storedPermissions) : null;
    }
  },
  
  /**
   * Dashboard fallback
   */
  dashboard: {
    getDashboardData(siteId = 'all') {
      console.log('Using fallback dashboard service');
      
      // Generate mock data for dashboard
      const todayUsage = Math.random() * 1000 + 500; // 500-1500 liters
      const yesterdayUsage = Math.random() * 1000 + 500; // 500-1500 liters
      const monthlyTotal = Math.random() * 30000 + 15000; // 15000-45000 liters
      const yearlyTotal = Math.random() * 365000 + 180000; // 180000-545000 liters
      
      // Generate daily data for the last 30 days
      const dailyData = Array.from({ length: 30 }, (_, i) => ({
        date: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        value: Math.random() * 1000 + 500 // 500-1500 liters
      }));
      
      // Generate monthly data for the last 12 months
      const monthlyData = Array.from({ length: 12 }, (_, i) => {
        const date = new Date();
        date.setMonth(date.getMonth() - (11 - i));
        return {
          date: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
          value: Math.random() * 30000 + 15000 // 15000-45000 liters
        };
      });
      
      // Generate hourly data for today and yesterday
      const todayHourlyData = Array.from({ length: 24 }, () => Math.random() * 100 + 10);
      const yesterdayHourlyData = Array.from({ length: 24 }, () => Math.random() * 100 + 10);
      
      return {
        todayUsage,
        yesterdayUsage,
        monthlyTotal,
        yearlyTotal,
        dailyData,
        monthlyData,
        todayHourlyData,
        yesterdayHourlyData
      };
    },
    
    getTodayUsage(siteId = 'all') {
      return Math.random() * 1000 + 500; // 500-1500 liters
    },
    
    getDailyUsage(days = 30, siteId = 'all') {
      return Array.from({ length: days }, (_, i) => ({
        date: new Date(Date.now() - (days - 1 - i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        value: Math.random() * 1000 + 500 // 500-1500 liters
      }));
    },
    
    getMonthlyUsage(months = 12, siteId = 'all') {
      return Array.from({ length: months }, (_, i) => {
        const date = new Date();
        date.setMonth(date.getMonth() - (months - 1 - i));
        return {
          date: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
          value: Math.random() * 30000 + 15000 // 15000-45000 liters
        };
      });
    }
  },
  
  /**
   * Site fallback
   */
  sites: {
    getAllSites() {
      console.log('Using fallback site service');
      return mockSites;
    },
    
    getSiteById(id) {
      console.log(`Using fallback site service for site ${id}`);
      return mockSites.find(site => site.id === parseInt(id)) || null;
    },
    
    getSiteDevices(id) {
      console.log(`Using fallback site service for site ${id} devices`);
      return mockDevices.filter(device => device.siteId === parseInt(id));
    },
    
    getSiteUsage(id, period = 'daily') {
      console.log(`Using fallback site service for site ${id} usage`);
      return mockDailyUsage;
    }
  },
  
  /**
   * Device fallback
   */
  devices: {
    getAllDevices() {
      console.log('Using fallback device service');
      return mockDevices;
    },
    
    getDeviceById(id) {
      console.log(`Using fallback device service for device ${id}`);
      return mockDevices.find(device => device.id.toString() === id.toString()) || null;
    },
    
    getDeviceReadings(id) {
      console.log(`Using fallback device service for device ${id} readings`);
      return mockReadings.filter(reading => reading.deviceId.toString() === id.toString());
    },
    
    getDeviceDailyUsage(id, params = {}) {
      // Generate mock daily usage data for the specified device
      const result = [];
      
      // If start and end dates are provided, use them
      const startDate = params.startDate ? new Date(params.startDate) : new Date();
      const endDate = params.endDate ? new Date(params.endDate) : new Date();
      
      // Set hours to 0 to compare dates properly
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      
      // Get the device to use its properties for realistic data generation
      const device = this.getDeviceById(id);
      
      // Loop through each day in the range
      const currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        // Generate a random usage value based on the device's properties
        // This creates more realistic mock data that varies by device
        const baseValue = device ? (parseInt(device.id) * 10) % 100 + 50 : 100;
        const randomFactor = 0.5 + Math.random();
        const totalUsage = baseValue * randomFactor;
        
        result.push({
          deviceId: id,
          date: new Date(currentDate).toISOString().split('T')[0],
          totalUsage: totalUsage,
          averageUsage: totalUsage / 24
        });
        
        // Move to next day
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      return result;
    }
  },
  
  /**
   * Reading fallback
   */
  readings: {
    getDeviceReadings(deviceId, params = {}) {
      console.log(`Using fallback reading service for device ${deviceId}`);
      return mockReadings.filter(reading => reading.deviceId.toString() === deviceId.toString());
    },
    
    getDailyUsage(params = {}) {
      console.log('Using fallback daily usage service');
      return mockDailyUsage;
    }
  }
};

export default fallbackService;
